import React, { useCallback, useMemo, useState } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Divider from 'components/Luxkit/Divider'
import Clickable from 'components/Common/Clickable'
import Switch from 'components/Luxkit/Switch/Switch'
import ListItem from 'components/Luxkit/List/ListItem'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import LineDoorIcon from 'components/Luxkit/Icons/line/LineDoorIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePackagesByRoom, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import MobileMultipleRoomsBookingFooter from './MobileMultipleRoomsBookingFooter'
import MobileMultipleRoomsBookingIndividualRooms from './MobileMultipleRoomsBookingIndividualRooms'
import MobileMultipleRoomsSameRoomContent from './MobileMultipleRoomsSameRoomContent'

const HeaderSection = styled(Pane)`
  padding: ${rem(20)} ${rem(20)} ${rem(16)};
`

const TogglesSection = styled(Pane)`
  padding: ${rem(4)} 0;
`

const DividerContainer = styled.div`
  padding: 0 ${rem(16)};
`

const ToggleButtonsContainer = styled(Group)`
  padding: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: ${rem(44)};
  padding: ${rem(4)} ${rem(16)};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  transition: background-color 0.2s, box-shadow 0.2s, color 0.2s, border-color 0.2s;
  outline-offset: 1px;

  &:focus {
    ${interactiveFocusStyle}
  }

  &.is-selected {
    background-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }
`

interface Props {
  rooms: Array<App.RoomOccupants>;
  isFlightsSelected: boolean;
  isMultiRoomConnected: boolean;
  multipleRoomPackages: PackageOptionViewByRoom;
}

function MobileMultipleRoomsBookingContent(props: Props) {
  const { rooms, isFlightsSelected, isMultiRoomConnected, multipleRoomPackages } = props

  const [mode, setMode] = useState<'individual' | 'same-room'>('individual')
  const dispatch = useOfferPageDispatch()

  const isFlightsAvailable = useMemo(() => Object.values(multipleRoomPackages).every(pkg => pkg?.flightsAvailable), [multipleRoomPackages])
  const hotelFlightsPrice = useMemo(() => (
    Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc
      const { hotelPrice, hotelMemberPrice, flightPrice, propertyFees = 0 } = pkg

      return acc + (hotelMemberPrice || hotelPrice) + propertyFees + flightPrice
    }, 0)
  ), [multipleRoomPackages])

  const onSelectIndividual = useCallback(() => {
    setMode('individual')
  }, [])

  const onSelectSame = useCallback(() => {
    setMode('same-room')
  }, [])

  const onToggleConnectedRooms = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [dispatch])

  const onSelectHotelFlights = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: !isFlightsSelected })
  }, [isFlightsSelected, dispatch])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        <HeaderSection>
          <Group direction="vertical" gap={16}>
            <Heading variant="heading4">Select your {pluralizeToString('room', rooms.length)}</Heading>
            <Group direction="vertical" gap={12}>
              <BodyText variant="medium">Choose the same package for all rooms, or customise each room individually.</BodyText>
              <ToggleButtonsContainer direction="horizontal" gap={4}>
                <ToggleButton className={cn({ 'is-selected': mode === 'individual' })} onClick={onSelectIndividual}>
                  <BodyText
                    variant="medium"
                    weight={mode === 'individual' ? 'bold' : undefined}
                    colour={mode === 'individual' ? 'neutral-eight' : 'neutral-one'}
                  >
                    Individually
                  </BodyText>
                </ToggleButton>
                <ToggleButton className={cn({ 'is-selected': mode === 'same-room' })} onClick={onSelectSame}>
                  <BodyText
                    variant="medium"
                    weight={mode === 'same-room' ? 'bold' : undefined}
                    colour={mode === 'same-room' ? 'neutral-eight' : 'neutral-one'}
                  >
                    Same for all rooms
                  </BodyText>
                </ToggleButton>
              </ToggleButtonsContainer>
            </Group>
            {mode === 'individual' && <MessageBanner
              kind="success"
              description="We've pre-selected the best-value rooms to get you started. Customise each room below."
            />}
          </Group>
        </HeaderSection>
        <TogglesSection>
          <ListItem
            title="Request connecting rooms"
            subtitle="Subject to hotel availability. Confirmed at check-in."
            startIcon={<LineDoorIcon />}
          >
            <Switch checked={isMultiRoomConnected} onChange={onToggleConnectedRooms} />
          </ListItem>
          {isFlightsAvailable && (<>
            <DividerContainer><Divider kind="primary" /></DividerContainer>
            <ListItem
              title="Add flights for all travellers"
              subtitle={hotelFlightsPrice ? <>From <FormatCurrency value={hotelFlightsPrice} /></> : undefined}
              startIcon={<LinePlaneDepartureIcon />}
            >
              <Switch checked={isFlightsSelected} onChange={onSelectHotelFlights} />
            </ListItem>
          </>)}
        </TogglesSection>
        <Pane>
          {mode === 'individual' && <MobileMultipleRoomsBookingIndividualRooms />}
          {mode === 'same-room' && (
            <MobileMultipleRoomsSameRoomContent />
          )}
        </Pane>
        <MobileMultipleRoomsBookingFooter />
      </Group>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  isFlightsSelected: state.flights,
  isMultiRoomConnected: state.isMultiRoomConnected,
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
}), MobileMultipleRoomsBookingContent)
